/* product.js — <product-form> custom element: variant selection, quantity stepper,
   sticky ATC. Add-to-cart submit is handled by cart-drawer.js (opens the drawer).
   Framework-free, deferred. */
(function () {
  'use strict';

  function a11y(key, fallback) {
    return window.puString ? window.puString(key, fallback) : fallback;
  }

  function money(cents) {
    // USD store. Adjust if multi-currency is enabled.
    return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 });
  }

  // POD SKU shape is style-COLOR-size (03-BLAC-S). Only the middle token is a color,
  // and only when it matches the selected size, so unrelated hyphens are ignored.
  var SKU_COLORS = { BLAC: 'Black', NAVY: 'Navy', WHIT: 'White', WHT: 'White', RED: 'Red', GREY: 'Grey', GRAY: 'Grey' };

  function skuGarmentColor(variant, optionValues) {
    var sku = variant && variant.sku;
    if (!sku) return '';
    var bits = String(sku).split('-');
    if (bits.length < 3) return '';
    var size = optionValues && optionValues.length ? String(optionValues[optionValues.length - 1]) : '';
    if (!size || bits[bits.length - 1].toUpperCase() !== size.toUpperCase()) return '';
    return SKU_COLORS[bits[1].toUpperCase()] || '';
  }

  // Shared by the product button and the sticky bar (setAtc copies the label).
  // Absent stock map keeps the old Add to Cart / Sold out split so other
  // product forms, including quick view, stay on that wording.
  function stockFor(variant, stockById) {
    if (!variant || !stockById) return null;
    var row = stockById[String(variant.id)];
    if (!row && variant.id != null) row = stockById[variant.id];
    if (!row) return { tracked: false, qty: null };
    return {
      tracked: row.tracked === true,
      qty: typeof row.qty === 'number' ? row.qty : null
    };
  }

  function atcButtonLabel(variant, inStockLabel, neverStocked, stockById, preorderTemplate) {
    var label = inStockLabel || 'Add to Cart';
    if (!variant || !variant.available) return 'Sold out';
    if (preorderTemplate) return 'Pre-order';
    var stock = stockFor(variant, stockById);
    if (!stock) return label;
    if (stock.tracked && typeof stock.qty === 'number' && stock.qty <= 0) {
      return neverStocked ? 'Pre-order' : 'Backorder';
    }
    return label;
  }

  function superMoney(cents) {
    var s = money(cents);
    var i = s.lastIndexOf('.');
    if (i === -1) return s;
    return s.slice(0, i) + '<sup class="money-super__cents">' + s.slice(i + 1) + '</sup>';
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-product-variants]');
      this.variants = data ? JSON.parse(data.textContent) : [];
      this.stockById = null;
      var stockEl = this.querySelector('[data-variant-stock]');
      if (stockEl && stockEl.textContent) {
        try {
          this.stockById = JSON.parse(stockEl.textContent);
        } catch (e) {
          this.stockById = null;
        }
      }
      this.neverStocked = this.getAttribute('data-never-stocked') === 'true';
      this.preorderTemplate = this.getAttribute('data-preorder-template') === 'true';
      this.idInput = this.querySelector('[data-variant-id]');
      this.priceEl = this.querySelector('[data-price]');
      this.compareEl = this.querySelector('[data-compare]');
      this.atc = this.querySelector('[data-atc]');
      this.atcText = this.querySelector('[data-atc-text]');
      this.addLabel = (this.atc && this.atc.getAttribute('data-add-label')) || this.getAttribute('data-add-to-cart') || 'Add to Cart';
      this.atcPrice = this.querySelector('[data-atc-price]');
      this.savingsEl = this.querySelector('[data-savings]');
      this.savingsChip = this.querySelector('[data-product-savings]');
      this.saleBadge = this.querySelector('[data-sale-badge]');
      this.saleBadgeText = this.querySelector('[data-sale-badge-text]');
      this.inventoryEl = this.querySelector('[data-inventory]');

      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-variant-picker]')) this.onVariantChange();
      });

      // Quantity stepper
      this.addEventListener('click', (e) => {
        var minus = e.target.closest('[data-qty-minus]');
        var plus = e.target.closest('[data-qty-plus]');
        if (!minus && !plus) return;
        var input = this.querySelector('[data-qty-input]');
        if (!input) return;
        var n = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, n + (plus ? 1 : -1));
      });

      this.updateAvailability();
      if (this.hasAttribute('data-buybox')) {
        var startId = this.idInput ? String(this.idInput.value) : '';
        var startVariant = this.variants.find(function (v) { return String(v.id) === startId; });
        if (startVariant) {
          this.syncSticky(startVariant);
          this.syncShipLine(startVariant);
        }
      }
      this.bindBuyWithPrime();
    }

    disconnectedCallback() {
      this.stopBuyWithPrime();
    }

    // Shopify injects delivery-promise-wc into #ProductForm on every PDP, and
    // may replace it on variant change. An empty host is 0px tall, so the
    // negative buy-box margin waits until the widget has height. Quick view
    // uses #QuickViewForm and is left alone. form.id is the variant input.
    bindBuyWithPrime() {
      this.stopBuyWithPrime();
      var root = this;
      var form = root.querySelector('#ProductForm');
      if (!form || form.getAttribute('id') !== 'ProductForm') return;
      if (typeof MutationObserver !== 'function' || typeof ResizeObserver !== 'function') return;

      var resizes = new Map();

      function hostHeight(host, entry) {
        var sizes = entry && entry.borderBoxSize;
        var box = sizes && (sizes[0] || sizes);
        if (box && typeof box.blockSize === 'number') return box.blockSize;
        return host.getBoundingClientRect().height;
      }

      function syncHost(host, entry) {
        if (hostHeight(host, entry) > 0) host.setAttribute('data-bwp-rendered', '');
        else host.removeAttribute('data-bwp-rendered');
      }

      function observeHost(host) {
        if (!host || host.nodeType !== 1 || resizes.has(host)) return;
        var current = root.querySelector('#ProductForm');
        if (!current || current.getAttribute('id') !== 'ProductForm' || !current.contains(host)) return;
        var ro = new ResizeObserver(function (entries) {
          syncHost(host, entries && entries[0]);
        });
        ro.observe(host);
        resizes.set(host, ro);
        syncHost(host);
      }

      function releaseHost(host) {
        if (!host || host.nodeType !== 1) return;
        var ro = resizes.get(host);
        if (ro) {
          ro.disconnect();
          resizes.delete(host);
        }
        host.removeAttribute('data-bwp-rendered');
      }

      function collectHosts(node, bucket) {
        if (!node || node.nodeType !== 1) return;
        if (node.matches && node.matches('delivery-promise-wc')) bucket.push(node);
        if (!node.querySelectorAll) return;
        node.querySelectorAll('delivery-promise-wc').forEach(function (el) { bucket.push(el); });
      }

      form.querySelectorAll('delivery-promise-wc').forEach(observeHost);

      var mo = new MutationObserver(function (records) {
        var removed = [];
        var added = [];
        records.forEach(function (record) {
          record.removedNodes.forEach(function (node) { collectHosts(node, removed); });
          record.addedNodes.forEach(function (node) { collectHosts(node, added); });
        });
        removed.forEach(releaseHost);
        added.forEach(observeHost);
      });
      mo.observe(form, { childList: true, subtree: true });

      this._stopBuyWithPrime = function () {
        mo.disconnect();
        resizes.forEach(function (ro, host) {
          ro.disconnect();
          host.removeAttribute('data-bwp-rendered');
        });
        resizes.clear();
      };
    }

    stopBuyWithPrime() {
      if (!this._stopBuyWithPrime) return;
      this._stopBuyWithPrime();
      this._stopBuyWithPrime = null;
    }

    // Variant compare-at wins. data-sale-compare is the sum of available
    // bundle item prices. Multi-variant bundles always expose that sum so
    // a variant without its own compare-at can still use it.
    saleCompareCents(variant) {
      if (!variant) return 0;
      if (variant.compare_at_price > variant.price) return variant.compare_at_price;
      var fallback = parseInt(this.getAttribute('data-sale-compare'), 10);
      if (fallback > variant.price) return fallback;
      return 0;
    }

    selectedOptions() {
      var pickers = this.querySelectorAll('[data-variant-picker] fieldset');
      return Array.from(pickers).map((fs) => {
        var checked = fs.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    // Disable option values that have no available variant given the earlier-position
    // selections (e.g. a size with no stock in the chosen color). If the current pick
    // becomes invalid, advance to the first available value. Also mirrors the chosen
    // value into the option legend ("Color: Navy").
    updateAvailability() {
      var groups = Array.from(this.querySelectorAll('[data-variant-picker] fieldset'));
      groups.forEach((fs, gi) => {
        var sel = this.selectedOptions();
        var inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
        inputs.forEach((input) => {
          var ok = this.variants.some((v) => {
            if (!v.available || v.options[gi] !== input.value) return false;
            for (var i = 0; i < gi; i++) { if (v.options[i] !== sel[i]) return false; }
            return true;
          });
          input.disabled = !ok;
          var chip = input.closest('.variant-picker__chip');
          if (chip) chip.classList.toggle('is-unavailable', !ok);
        });
        var checked = fs.querySelector('input:checked');
        if (checked && checked.disabled) {
          var next = inputs.find((i) => !i.disabled);
          if (next) next.checked = true;
        }
        var out = fs.querySelector('[data-selected-value]');
        var now = fs.querySelector('input:checked');
        if (out && now) out.textContent = now.value;
      });
    }

    onVariantChange() {
      this.updateAvailability();
      var opts = this.selectedOptions();
      var variant = this.variants.find((v) =>
        v.options.every((o, i) => o === opts[i])
      );

      if (!variant) {
        if (missingOptionFieldset(this)) this.setAtc(true, this.addLabel);
        else this.setAtc(false, 'Unavailable');
        this.syncShipLine(null);
        this.emitVariantChange(null);
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      // Move the gallery to this variant's image (the gallery listens for this event).
      if (variant.featured_media && variant.featured_media.id != null) {
        document.dispatchEvent(new CustomEvent('product:variant-media', { detail: { mediaId: variant.featured_media.id } }));
      }
      // URL sync
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
      // Price. The struck amount is the variant compare-at, or the bundle
      // item sum stored on data-sale-compare. The sale badge stays on the
      // variant compare-at only.
      var variantOnSale = variant.compare_at_price > variant.price;
      var compareCents = this.saleCompareCents(variant);
      var onSale = compareCents > variant.price;
      if (this.priceEl) {
        if (this.priceEl.hasAttribute('data-superscript')) this.priceEl.innerHTML = superMoney(variant.price);
        else this.priceEl.textContent = money(variant.price);
        this.priceEl.classList.toggle('is-sale', onSale);
      }
      if (this.compareEl) {
        if (onSale) {
          if (this.compareEl.hasAttribute('data-superscript')) this.compareEl.innerHTML = superMoney(compareCents);
          else this.compareEl.textContent = money(compareCents);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
      if (this.saleBadge) {
        var saleWrap = this.saleBadge.closest('.shopify-block');
        if (variantOnSale) {
          var pctOff = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          if (this.saleBadgeText) this.saleBadgeText.textContent = pctOff + '% off';
          this.saleBadge.hidden = false;
          if (saleWrap) saleWrap.hidden = false;
        } else {
          this.saleBadge.hidden = true;
          if (saleWrap) saleWrap.hidden = true;
        }
      }
      if (this.savingsEl) {
        if (variantOnSale) {
          var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
          this.savingsEl.textContent = pct + '% off';
          this.savingsEl.hidden = false;
        } else {
          this.savingsEl.hidden = true;
        }
      }
      if (this.savingsChip) {
        if (onSale) {
          var amtEl = this.savingsChip.querySelector('[data-savings-amount]');
          var pctEl = this.savingsChip.querySelector('[data-savings-percent]');
          var savedPct = Math.round(((compareCents - variant.price) / compareCents) * 100);
          if (amtEl) amtEl.textContent = money(compareCents - variant.price);
          if (pctEl) pctEl.textContent = savedPct > 0 ? '(' + savedPct + '%)' : '';
          this.savingsChip.hidden = false;
          var chipWrap = this.savingsChip.closest('.shopify-block');
          if (chipWrap) chipWrap.hidden = false;
        } else {
          this.savingsChip.hidden = true;
          var chipWrapOff = this.savingsChip.closest('.shopify-block');
          if (chipWrapOff) chipWrapOff.hidden = true;
        }
      }
      if (this.atcPrice) this.atcPrice.textContent = variant.available ? ' · ' + money(variant.price) : '';
      this.syncGalleryBadge(variant);
      this.syncSticky(variant);
      this.syncShipLine(variant);
      // Availability
      this.setAtc(variant.available, atcButtonLabel(variant, this.addLabel, this.neverStocked, this.stockById, this.preorderTemplate));
      this.updateInventory(variant);
      this.emitVariantChange(variant);
    }

    // Automatic "Ships in 1-2 days" chip. Rendered with data-ship-line when
    // variants disagree on requires_shipping, or when preorder-state differs
    // across variants. A digital-only product, and a product whose every
    // variant is Pre-order or Backorder, omits the chip in Liquid, so this
    // is a no-op there. data-ship-hold is that snippet's token per variant
    // id (true or backorder). Any token hides the chip, except a
    // print-on-demand line (data-ship-pod), which keeps the 14-day promise.
    // In-stock ids are absent and keep the line. The button label is not read from this map.
    // Shipping & returns uses the shipping flag only. data-ship-details is the
    // shipping sentences. data-ship-accordion is the whole row when that
    // paragraph has no separate returns sentence. Both are rendered only when
    // variants disagree on shipping, and the accordion sits outside this form,
    // so only the buy box updates it.
    shipHoldMap() {
      if (this._shipHoldMap) return this._shipHoldMap;
      var el = this.querySelector('[data-ship-hold]');
      var map = {};
      if (el && el.textContent) {
        try {
          map = JSON.parse(el.textContent) || {};
        } catch (e) {
          map = {};
        }
      }
      this._shipHoldMap = map;
      return map;
    }

    syncShipLine(variant) {
      var line = this.querySelector('[data-ship-line]');
      var ships = null;
      if (variant) {
        ships = variant.requires_shipping;
        if (ships == null) ships = variant.requiresShipping;
      }
      if (line) {
        var hold = false;
        if (variant && !line.hasAttribute('data-ship-pod')) {
          var map = this.shipHoldMap();
          var token = map[String(variant.id)];
          if (token == null && variant.id != null) token = map[variant.id];
          hold = token != null && String(token).trim() !== '';
        }
        if (!variant || hold) line.hidden = true;
        else if (ships != null) line.hidden = ships === false || ships === 'false';
      }
      if (!this.hasAttribute('data-buybox')) return;
      if (variant && ships == null) return;
      var hideShip = !variant || ships === false || ships === 'false';
      document.querySelectorAll('[data-ship-details], [data-ship-accordion]').forEach(function (el) {
        el.hidden = hideShip;
      });
    }

    // Gift with Donation listens for this. product:variant-media only fires
    // when the variant has a featured image, so a sold-out variant would be missed.
    emitVariantChange(variant) {
      this.dispatchEvent(new CustomEvent('product:variant-change', {
        bubbles: true,
        detail: { variant: variant }
      }));
    }

    updateInventory(variant) {
      if (!this.inventoryEl) return;
      var threshold = parseInt(this.inventoryEl.getAttribute('data-threshold'), 10) || 0;
      var textEl = this.inventoryEl.querySelector('[data-inventory-text]');
      var tracked = variant.inventory_management != null && variant.inventory_management !== '';
      var qty = variant.inventory_quantity;
      if (variant.available && tracked && qty > 0 && qty <= threshold) {
        if (textEl) {
          var onlyLeft = this.inventoryEl.getAttribute('data-only-left');
          textEl.textContent = onlyLeft
            ? onlyLeft.split('__COUNT__').join(String(qty))
            : 'Only ' + qty + ' left';
        }
        this.inventoryEl.classList.add('is-low');
        this.inventoryEl.hidden = false;
      } else {
        this.inventoryEl.hidden = true;
        this.inventoryEl.classList.remove('is-low');
      }
    }

    // Gallery corner badge follows compare-at. Only the PDP buy box owns it.
    syncGalleryBadge(variant) {
      if (!this.hasAttribute('data-buybox') || !variant) return;
      var onSale = variant.compare_at_price > variant.price;
      var saleBadge = document.querySelector('[data-gallery-sale]');
      var soldBadge = document.querySelector('[data-gallery-sold]');
      if (saleBadge) saleBadge.hidden = !onSale;
      if (soldBadge) soldBadge.hidden = onSale || variant.available;
    }

    // Sticky bar lives outside <product-form>. Only the PDP buy box drives it
    // (quick view is a second product-form and must not overwrite the bar).
    syncSticky(variant) {
      if (!this.hasAttribute('data-buybox') || !variant) return;
      var priceEl = document.querySelector('[data-sticky-price]');
      if (priceEl) priceEl.textContent = money(variant.price);
      var compareWrap = document.querySelector('[data-sticky-compare]');
      var compareValue = document.querySelector('[data-sticky-compare-value]');
      var compareCents = this.saleCompareCents(variant);
      var stickyOnSale = compareCents > variant.price;
      if (compareWrap) {
        if (stickyOnSale) {
          if (compareValue) compareValue.textContent = money(compareCents);
          compareWrap.hidden = false;
        } else {
          compareWrap.hidden = true;
        }
      }
      var saveWrap = document.querySelector('[data-sticky-save]');
      var saveValue = document.querySelector('[data-sticky-save-value]');
      if (saveWrap) {
        if (stickyOnSale) {
          if (saveValue) saveValue.textContent = money(compareCents - variant.price);
          saveWrap.hidden = false;
        } else {
          saveWrap.hidden = true;
        }
      }
      var variantEl = document.querySelector('[data-sticky-variant]');
      if (!variantEl) return;
      var parts = [];
      (variant.options || []).forEach(function (option) {
        if (option && option !== 'Default Title') parts.push(option);
      });
      // Size-only POD tees (no Color option) store the garment color in the SKU,
      // e.g. 03-BLAC-S. Keep Color · Size. Products that already have a Color
      // option leave this attribute off and keep the joined option values.
      if (variantEl.getAttribute('data-sku-color') === 'true') {
        var garment = skuGarmentColor(variant, parts);
        if (garment && parts[0] !== garment) parts.unshift(garment);
      }
      if (parts.length) {
        variantEl.textContent = parts.join(' · ');
        variantEl.hidden = false;
      } else {
        variantEl.hidden = true;
      }
    }

    setAtc(enabled, label) {
      if (this.atc) {
        this.atc.disabled = !enabled;
        if (this.atcText) this.atcText.textContent = label;
      }
      // Quick view only. The note is absent on the product page, so the
      // button and sticky bar keep the label this function already set.
      var preorderNote = this.querySelector('[data-preorder-note]');
      if (preorderNote) preorderNote.hidden = label !== preorderNote.getAttribute('data-preorder-label');
      if (!this.hasAttribute('data-buybox')) return;
      var stickyBtn = document.querySelector('[data-sticky-atc] [data-sticky-add]');
      if (!stickyBtn) return;
      stickyBtn.disabled = !enabled;
      var stickyText = stickyBtn.querySelector('[data-sticky-add-text]');
      if (stickyText) stickyText.textContent = label;
    }
  }

  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  // Related products: fetch this section rendered with the Recommendations API, swap it in.
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      var url = this.dataset.url;
      if (!url || this.classList.contains('is-loaded')) return;
      fetch(url)
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((text) => {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var fresh = doc.querySelector('[data-recs]');
          if (!fresh || !fresh.querySelector('.tb-product-grid')) return;
          this.innerHTML = fresh.innerHTML;
          this.classList.add('is-loaded', 'section-spacing');
          this.querySelectorAll('[data-reveal-group]').forEach((g) => g.classList.add('is-visible'));
        })
        .catch(() => { /* leave empty */ });
    }
  }
  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

  // Sticky ATC: reveal once the main buy button scrolls above the sticky header.
  // The button itself submits #ProductForm (form attribute), so it shares the
  // cart-drawer.js add path, including the selected variant id and quantity.
  // True while the cart drawer is open. Desktop shifts the hub; mobile hides
  // it. The drawer does not write --sticky-atc-height, so keep the last
  // sticky-bar measurement until the drawer closes.
  function drawerIsOpen() {
    if (document.body.classList.contains('has-cart-drawer')) return true;
    var drawer = document.querySelector('cart-drawer');
    return !!(drawer && drawer.classList.contains('is-open'));
  }

  // --sticky-atc-height is the bar above the safe area, measured at every
  // breakpoint (desktop sticky ATC is the same bar). base.css adds the
  // safe-area and a gap when it raises .kl-hub-launcher. The cart drawer does
  // not use this variable.
  // Chrome blocks aria-hidden and inert while a control inside the bar still has focus.
  function releaseStickyFocus(sticky) {
    var active = document.activeElement;
    if (!active || !sticky.contains(active)) return;
    var mainAtc = document.querySelector('[data-buybox] [data-atc]');
    if (mainAtc && !mainAtc.disabled && !sticky.contains(mainAtc)) {
      mainAtc.focus({ preventScroll: true });
    }
    if (sticky.contains(document.activeElement) && document.activeElement.blur) {
      document.activeElement.blur();
    }
  }

  function syncStickySpace(sticky, on) {
    var root = document.documentElement;
    if (!on) {
      releaseStickyFocus(sticky);
      root.style.setProperty('--sticky-atc-pad', '0px');
      document.body.classList.remove('has-sticky-atc');
      if (!document.body.classList.contains('template-cart') && !drawerIsOpen()) {
        document.body.style.setProperty('--sticky-atc-height', '0px');
      }
      sticky.setAttribute('aria-hidden', 'true');
      sticky.setAttribute('inert', '');
      scheduleHubDonateClear();
      return;
    }
    sticky.removeAttribute('aria-hidden');
    sticky.removeAttribute('inert');
    document.body.classList.add('has-sticky-atc');
    var cs = window.getComputedStyle(sticky);
    var safe = parseFloat(cs.paddingBottom) || 0;
    var aboveSafe = Math.max(0, Math.ceil(sticky.offsetHeight - safe));
    var bottom = parseFloat(cs.bottom) || 0;
    /* Pad matches the bar. An extra 12px left a strip between the footer
       and the bar at the end of the page. The Klaviyo lift still uses
       --sticky-chrome-gap; this reserve does not. */
    root.style.setProperty('--sticky-atc-pad', Math.ceil(sticky.offsetHeight + bottom) + 'px');
    if (!drawerIsOpen()) document.body.style.setProperty('--sticky-atc-height', aboveSafe + 'px');
    scheduleHubDonateClear();
  }

  /* Phone only. The Customer Hub launcher is raised just above the sticky
     buy bar, which puts it on the full-width Gift with Donation button when
     that button scrolls through the corner. Hide the launcher for that
     overlap and show it again once the button has moved clear. Cart-open
     hiding stays in base.css and is unchanged. */
  var scheduleHubDonateClear = function () {};

  function initHubDonateClear() {
    var mq = window.matchMedia('(max-width: 749px)');
    var ticking = false;
    function sync() {
      ticking = false;
      var root = document.documentElement;
      if (!mq.matches) {
        root.removeAttribute('data-hub-clear-donate');
        return;
      }
      var launcher = document.querySelector('.kl-hub-launcher');
      var box = launcher && launcher.getBoundingClientRect();
      if (!box || box.width < 8 || box.height < 8) {
        root.removeAttribute('data-hub-clear-donate');
        return;
      }
      var nodes = document.querySelectorAll('[data-gwd-donate], .variant-picker__chips');
      var hit = false;
      for (var i = 0; i < nodes.length; i++) {
        var r = nodes[i].getBoundingClientRect();
        if (r.width < 1 || r.height < 1) continue;
        if (r.left < box.right && r.right > box.left && r.top < box.bottom && r.bottom > box.top) {
          hit = true;
          break;
        }
      }
      if (hit) root.setAttribute('data-hub-clear-donate', 'true');
      else root.removeAttribute('data-hub-clear-donate');
    }
    scheduleHubDonateClear = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(sync);
    };
    window.addEventListener('scroll', scheduleHubDonateClear, { passive: true });
    window.addEventListener('resize', scheduleHubDonateClear);
    if (mq.addEventListener) mq.addEventListener('change', scheduleHubDonateClear);
    if (window.MutationObserver && document.body) {
      new MutationObserver(function (records) {
        for (var i = 0; i < records.length; i++) {
          var added = records[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (!node || node.nodeType !== 1) continue;
            if ((node.matches && node.matches('.kl-hub-launcher, .kl-hub-launcher-container')) ||
                (node.querySelector && node.querySelector('.kl-hub-launcher'))) {
              scheduleHubDonateClear();
              return;
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }
    scheduleHubDonateClear();
  }

  function initStickyAtc() {
    document.querySelectorAll('[data-sticky-atc]').forEach(function (el) {
      if (el.dataset.stickyBound !== '1') return;
      if (el._stickyObs) el._stickyObs.disconnect();
      if (el._stickyUnbind) el._stickyUnbind();
      el.remove();
    });
    var sticky = document.querySelector('[data-sticky-atc]');
    var buybox = document.querySelector('[data-buybox]');
    var form = document.getElementById('ProductForm');
    var trigger = buybox ? buybox.querySelector('[data-atc]') : null;
    if (!sticky || !form || !trigger) return;
    sticky.dataset.stickyBound = '1';

    // Ancestor transforms (scroll-reveal, the theme editor) turn position:fixed
    // into something that scrolls away. Park the bar on <body>.
    if (sticky.parentElement !== document.body) document.body.appendChild(sticky);

    var header = document.querySelector('.site-header');
    var ticking = false;

    // Every width: measure the button against the live header. The bar stays
    // hidden until the button bottom has moved above the header.
    function revealSticky() {
      var topInset = header ? header.offsetHeight : 0;
      var rect = trigger.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        sticky.classList.remove('is-visible');
        syncStickySpace(sticky, false);
        return;
      }
      var show = rect.bottom <= topInset + 1;
      sticky.classList.toggle('is-visible', show);
      syncStickySpace(sticky, show);
    }

    function scheduleSticky() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        revealSticky();
      });
    }

    revealSticky();
    window.addEventListener('scroll', scheduleSticky, { passive: true });
    window.addEventListener('resize', scheduleSticky);
    sticky._stickyUnbind = function () {
      window.removeEventListener('scroll', scheduleSticky);
      window.removeEventListener('resize', scheduleSticky);
    };

    if ('IntersectionObserver' in window) {
      if (sticky._stickyObs) sticky._stickyObs.disconnect();
      var obs = new IntersectionObserver(scheduleSticky, { threshold: [0, 1] });
      sticky._stickyObs = obs;
      obs.observe(trigger);
    }
  }

  function missingOptionFieldset(root) {
    if (!root || !root.querySelectorAll) return null;
    var groups = root.querySelectorAll('[data-variant-picker] fieldset');
    for (var i = 0; i < groups.length; i++) {
      var radios = groups[i].querySelectorAll('input[type="radio"]');
      if (!radios.length) continue;
      if (!groups[i].querySelector('input[type="radio"]:checked')) return groups[i];
    }
    return null;
  }

  function guardUnselectedOption(event) {
    var form = event.target;
    // The variant input is named id, so form.id is that input, not the attribute.
    if (!form || form.getAttribute('id') !== 'ProductForm') return;
    var missing = missingOptionFieldset(form.closest('[data-buybox]') || form);
    if (!missing) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    missing.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    var focusable = missing.querySelector('input[type="radio"]:not(:disabled)') || missing.querySelector('input[type="radio"]');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  // Editorial gallery: scroll-snap viewport + thumb rail + arrows + counter + lightbox.
  function initGallery() {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('[data-gallery]').forEach((gallery) => {
      if (gallery.dataset.galleryBound) return;
      gallery.dataset.galleryBound = '1';
      var viewport = gallery.querySelector('[data-gallery-viewport]');
      if (!viewport) return;
      var slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
      var thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
      var counter = gallery.querySelector('[data-gallery-counter]');
      var prev = gallery.querySelector('[data-gallery-prev]');
      var next = gallery.querySelector('[data-gallery-next]');
      var behavior = reduce ? 'auto' : 'smooth';
      var current = 0;

      function activeIndex() {
        var mid = viewport.scrollLeft + viewport.clientWidth / 2;
        var acc = 0;
        for (var i = 0; i < slides.length; i++) {
          acc += slides[i].getBoundingClientRect().width;
          if (mid <= acc) return i;
        }
        return slides.length - 1;
      }
      function sync() {
        current = activeIndex();
        thumbs.forEach((t, i) => t.setAttribute('aria-current', i === current ? 'true' : 'false'));
        if (counter) counter.textContent = (current + 1) + ' / ' + slides.length;
        if (prev) prev.toggleAttribute('disabled', current <= 0);
        if (next) next.toggleAttribute('disabled', current >= slides.length - 1);
        revealActiveThumb();
      }
      /* scrollIntoView would move the page too. Nudge the rail only, on
         either axis, the same way the mobile row already does. */
      function revealActiveThumb() {
        var rail = gallery.querySelector('[data-gallery-thumbs]');
        var thumb = thumbs[current];
        if (!rail || !thumb || !thumb.getClientRects().length) return;
        var railBox = rail.getBoundingClientRect();
        var box = thumb.getBoundingClientRect();
        var left = rail.scrollLeft;
        var top = rail.scrollTop;
        if (box.left < railBox.left - 1) left += box.left - railBox.left;
        else if (box.right > railBox.right + 1) left += box.right - railBox.right;
        if (box.top < railBox.top - 1) top += box.top - railBox.top;
        else if (box.bottom > railBox.bottom + 1) top += box.bottom - railBox.bottom;
        if (left !== rail.scrollLeft || top !== rail.scrollTop) {
          rail.scrollTo({ left: left, top: top, behavior: behavior });
        }
      }
      function goTo(i) {
        var clamped = Math.max(0, Math.min(slides.length - 1, i));
        var slide = slides[clamped];
        if (slide) viewport.scrollTo({ left: slide.offsetLeft, behavior: behavior });
      }

      thumbs.forEach((t) => t.addEventListener('click', () => goTo(parseInt(t.dataset.index, 10) || 0)));
      if (prev) prev.addEventListener('click', () => goTo(current - 1));
      if (next) next.addEventListener('click', () => goTo(current + 1));
      viewport.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      // Jump to a variant's image when the buy box reports a variant change.
      document.addEventListener('product:variant-media', (e) => {
        var id = e.detail && e.detail.mediaId;
        if (id == null) return;
        var idx = slides.findIndex((s) => String(s.dataset.mediaId) === String(id));
        if (idx >= 0) goTo(idx);
      });
      sync();

      // Thumb rail scroll arrows (desktop). Shown only when the rail overflows.
      var thumbsEl = gallery.querySelector('[data-gallery-thumbs]');
      var railUp = gallery.querySelector('[data-thumbs-up]');
      var railDown = gallery.querySelector('[data-thumbs-down]');
      if (thumbsEl && (railUp || railDown)) {
        function railStep() {
          var t = thumbsEl.querySelector('.product-gallery__thumb');
          var gap = parseFloat(getComputedStyle(thumbsEl).rowGap) || 0;
          return t ? (t.getBoundingClientRect().height + gap) * 2 : thumbsEl.clientHeight * 0.8;
        }
        function syncRail() {
          var scrollable = thumbsEl.scrollHeight > thumbsEl.clientHeight + 2;
          if (railUp) railUp.hidden = !scrollable;
          if (railDown) railDown.hidden = !scrollable;
          if (!scrollable) return;
          var atTop = thumbsEl.scrollTop <= 2;
          var atBottom = thumbsEl.scrollTop >= thumbsEl.scrollHeight - thumbsEl.clientHeight - 2;
          if (railUp) railUp.toggleAttribute('disabled', atTop);
          if (railDown) railDown.toggleAttribute('disabled', atBottom);
        }
        if (railUp) railUp.addEventListener('click', () => thumbsEl.scrollBy({ top: -railStep(), behavior: behavior }));
        if (railDown) railDown.addEventListener('click', () => thumbsEl.scrollBy({ top: railStep(), behavior: behavior }));
        thumbsEl.addEventListener('scroll', syncRail, { passive: true });
        window.addEventListener('resize', syncRail, { passive: true });
        syncRail();
      }

      // Lightbox. Every product.media slide stays in order. Hosted video is
      // first-class: data-media-type="video" and/or [data-gallery-video]. A poster
      // data-zoom-src keeps attribute-only steppers from jumping over it. The player
      // wins over that still whenever the slide is a video.
      var box = document.querySelector('[data-lightbox]');
      if (box && typeof box.showModal === 'function' && box.dataset.lightboxBound !== '1') {
        box.dataset.lightboxBound = '1';
        var img = box.querySelector('[data-lightbox-img]');
        var lbVideo = box.querySelector('[data-lightbox-video]');
        var lbFigure = box.querySelector('[data-lightbox-figure]');
        var lbCounter = box.querySelector('[data-lightbox-counter]');
        var lbPrev = box.querySelector('[data-lightbox-prev]');
        var lbNext = box.querySelector('[data-lightbox-next]');
        var lbIndex = 0;
        function slideVideo(s) {
          return s ? s.querySelector('[data-gallery-video]') : null;
        }
        function isVideoSlide(s) {
          if (!s) return false;
          if (s.getAttribute('data-media-type') === 'video') return true;
          return !!slideVideo(s);
        }
        function canShow(s) {
          if (!s) return false;
          if (isVideoSlide(s)) return true;
          return s.hasAttribute('data-zoom-src');
        }
        // Array position wins. data-index is only a fallback, and 0 is a real index.
        function slideIndex(s) {
          if (!s) return -1;
          var i = slides.indexOf(s);
          if (i !== -1) return i;
          var raw = s.getAttribute('data-index');
          if (raw == null || raw === '') return -1;
          var n = parseInt(raw, 10);
          return isFinite(n) ? n : -1;
        }
        function videoSources(galleryVideo) {
          var list = [];
          galleryVideo.querySelectorAll('source').forEach((src) => {
            var url = src.getAttribute('src');
            if (url) list.push({ url: url, type: src.getAttribute('type') || '' });
          });
          if (!list.length) {
            var direct = galleryVideo.getAttribute('src') || galleryVideo.currentSrc || '';
            if (direct) list.push({ url: direct, type: '' });
          }
          return list;
        }
        function pauseGalleryVideos() {
          gallery.querySelectorAll('[data-gallery-video]').forEach((v) => { v.pause(); });
        }
        function showLightboxVideo(galleryVideo, posterFallback) {
          if (!lbVideo || !galleryVideo) return;
          var sources = videoSources(galleryVideo);
          var sig = sources.map((src) => src.url + '|' + src.type).join('||');
          if (lbVideo.dataset.sig !== sig) {
            lbVideo.pause();
            while (lbVideo.firstChild) lbVideo.removeChild(lbVideo.firstChild);
            sources.forEach((src) => {
              var el = document.createElement('source');
              el.src = src.url;
              if (src.type) el.type = src.type;
              lbVideo.appendChild(el);
            });
            lbVideo.poster = galleryVideo.getAttribute('poster') || posterFallback || '';
            lbVideo.setAttribute('aria-label', galleryVideo.getAttribute('aria-label') || a11y('productVideo', 'Product video'));
            lbVideo.dataset.sig = sig;
            lbVideo.load();
          }
          lbVideo.muted = true;
          if (!reduce) lbVideo.play().catch(() => {});
        }
        function paint(i) {
          lbIndex = Math.max(0, Math.min(slides.length - 1, i));
          var s = slides[lbIndex];
          if (!s) return;
          pauseGalleryVideos();
          var galleryVideo = slideVideo(s);
          var kind = isVideoSlide(s) && galleryVideo ? 'video' : 'image';
          if (lbFigure) lbFigure.setAttribute('data-lightbox-kind', kind);
          box.classList.toggle('is-video', kind === 'video');
          if (kind === 'video') {
            showLightboxVideo(galleryVideo, s.getAttribute('data-zoom-src') || '');
          } else if (img) {
            if (lbVideo) lbVideo.pause();
            img.src = s.getAttribute('data-zoom-src') || '';
            img.alt = s.getAttribute('data-zoom-alt') || '';
          }
          if (lbCounter) lbCounter.textContent = (lbIndex + 1) + ' / ' + slides.length;
        }
        function step(dir) {
          var i = lbIndex + dir;
          while (i >= 0 && i < slides.length && !canShow(slides[i])) i += dir;
          if (i >= 0 && i < slides.length) paint(i);
        }
        function openAt(s) {
          var i = slideIndex(s);
          if (i < 0 || !canShow(slides[i])) return;
          pauseGalleryVideos();
          paint(i);
          if (!box.open) box.showModal();
        }
        slides.forEach((s) => s.addEventListener('click', (e) => {
          if (!canShow(s)) return;
          // Play/pause and mute stay inline. The video surface and image slides open here.
          if (e.target.closest('[data-video-toggle], [data-video-mute]')) return;
          openAt(s);
        }));
        if (lbPrev) lbPrev.addEventListener('click', () => step(-1));
        if (lbNext) lbNext.addEventListener('click', () => step(1));
        var lbClose = box.querySelector('[data-lightbox-close]');
        if (lbClose) lbClose.addEventListener('click', () => box.close());
        box.addEventListener('click', (e) => { if (e.target === box) box.close(); });
        box.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
          e.preventDefault();
          step(e.key === 'ArrowLeft' ? -1 : 1);
        });
        // On close, stop the lightbox player and snap the inline gallery to the last slide.
        box.addEventListener('close', () => {
          if (lbVideo) lbVideo.pause();
          goTo(lbIndex);
        });
      }
    });
  }

  // Share: native share sheet where available, else copy the URL to the clipboard.
  function initShare() {
    document.querySelectorAll('[data-share]').forEach((btn) => {
      if (btn.dataset.shareBound) return;
      btn.dataset.shareBound = '1';
      var label = btn.querySelector('[data-share-label]');
      var original = label ? label.textContent : '';
      var originalLabel = btn.getAttribute('aria-label') || '';
      btn.addEventListener('click', async () => {
        var url = btn.getAttribute('data-share-url') || window.location.href;
        var title = btn.getAttribute('data-share-title') || document.title;
        if (navigator.share) {
          try { await navigator.share({ title: title, url: url }); } catch (e) { /* dismissed */ }
          return;
        }
        try {
          await navigator.clipboard.writeText(url);
          var copied = btn.getAttribute('data-share-copied') || a11y('linkCopied', 'Link copied');
          var tools = btn.closest('.product-gallery__tools');
          var shareRoot = btn.closest('.product-share');
          var live = tools ? tools.querySelector('[data-share-live]') : null;
          if (!live && shareRoot) live = shareRoot.querySelector('[data-share-live]');
          if (live) {
            live.textContent = copied;
          } else if (label) {
            label.textContent = copied;
            btn.classList.add('is-copied');
          } else if (btn.hasAttribute('data-share-copied')) {
            btn.setAttribute('aria-label', copied);
            btn.classList.add('is-copied');
          }
          if (btn._shareTimer) clearTimeout(btn._shareTimer);
          btn._shareTimer = setTimeout(() => {
            if (live) {
              live.textContent = '';
            } else if (label) {
              label.textContent = original;
              btn.classList.remove('is-copied');
            } else if (btn.hasAttribute('data-share-copied')) {
              btn.setAttribute('aria-label', originalLabel);
              btn.classList.remove('is-copied');
            }
          }, 2000);
        } catch (e) { /* clipboard blocked */ }
      });
    });
  }

  // Gallery heart. Clicks the Klaviyo Add to favorites button in the buy box
  // (the same control the hidden Favorites block keeps mounted) and mirrors
  // that button's active state onto aria-pressed. The heart is painted with
  // the gallery so a late Klaviyo inject does not leave a blank chip. A tap
  // before that button exists is replayed once the control mounts. If Klaviyo
  // never mounts, the heart is hidden again so it is not a dead control.
  var klaviyoShadowWatched = typeof WeakSet === 'function' ? new WeakSet() : null;
  var klaviyoControlSelector = 'button, [role="button"], a';
  var klaviyoFavoriteWaitMs = 9000;
  var klaviyoFavoriteTimer = null;
  var klaviyoFavoriteGaveUp = false;

  function klaviyoFavoriteHost() {
    return document.querySelector('.buybox-favorites klaviyo-favorites-button') || document.querySelector('klaviyo-favorites-button');
  }
  function klaviyoQuery(host, selector) {
    if (!host) return null;
    var found = host.querySelector(selector);
    if (found) return found;
    if (host.shadowRoot) return host.shadowRoot.querySelector(selector);
    return null;
  }
  function klaviyoFavoriteControl() {
    return klaviyoQuery(klaviyoFavoriteHost(), klaviyoControlSelector);
  }
  function klaviyoFavoriteOn(host) {
    if (!host) return false;
    var btn = klaviyoQuery(host, klaviyoControlSelector);
    if (btn) {
      var pressed = btn.getAttribute('aria-pressed');
      if (pressed === 'true') return true;
      if (pressed === 'false') return false;
      if (btn.getAttribute('aria-checked') === 'true') return true;
    }
    var icon = klaviyoQuery(host, '[class*="wishlist-icon"]');
    if (!icon) return false;
    var cls = icon.getAttribute('class') || '';
    if (cls.indexOf('--inactive') !== -1) return false;
    if (cls.indexOf('--active') !== -1) return true;
    return false;
  }
  function concealKlaviyoControls(host) {
    if (!host || !document.querySelector('.buybox-favorites--off')) return;
    [host, host.shadowRoot].forEach(function (root) {
      if (!root) return;
      root.querySelectorAll(klaviyoControlSelector).forEach(function (btn) {
        if (btn.getAttribute('tabindex') !== '-1') btn.setAttribute('tabindex', '-1');
      });
    });
  }
  function watchKlaviyoShadow(host) {
    if (!host || !host.shadowRoot || !klaviyoShadowWatched || klaviyoShadowWatched.has(host.shadowRoot)) return;
    klaviyoShadowWatched.add(host.shadowRoot);
    new MutationObserver(syncGalleryHearts).observe(host.shadowRoot, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'aria-pressed', 'aria-checked']
    });
  }
  function syncGalleryHearts() {
    var host = klaviyoFavoriteHost();
    var btn = klaviyoFavoriteControl();
    if (btn) {
      klaviyoFavoriteGaveUp = false;
      if (klaviyoFavoriteTimer) {
        clearTimeout(klaviyoFavoriteTimer);
        klaviyoFavoriteTimer = null;
      }
    }
    concealKlaviyoControls(host);
    watchKlaviyoShadow(host);
    var pending = false;
    document.querySelectorAll('[data-gallery-favorite]').forEach(function (heart) {
      if (heart.dataset.favoritePending === '1') pending = true;
    });
    if (pending && btn) {
      document.querySelectorAll('[data-gallery-favorite]').forEach(function (heart) {
        heart.dataset.favoritePending = '';
      });
      btn.click();
    }
    var on = btn ? klaviyoFavoriteOn(host) : false;
    var unavailable = !btn && klaviyoFavoriteGaveUp;
    document.querySelectorAll('[data-gallery-favorite]').forEach(function (heart) {
      if (unavailable) {
        heart.dataset.favoritePending = '';
        heart.hidden = true;
        if (document.activeElement === heart && typeof heart.blur === 'function') heart.blur();
        return;
      }
      heart.hidden = false;
      heart.setAttribute('aria-pressed', on ? 'true' : 'false');
      var next = on ? heart.getAttribute('data-label-added') : heart.getAttribute('data-label-add');
      if (next) heart.setAttribute('aria-label', next);
    });
    document.querySelectorAll('.product-gallery__tools').forEach(function (tools) {
      var visible = false;
      tools.querySelectorAll('.product-gallery__tool').forEach(function (tool) {
        if (!tool.hidden) visible = true;
      });
      tools.hidden = !visible;
    });
  }
  function initGalleryFavorite() {
    document.querySelectorAll('[data-gallery-favorite]').forEach((heart) => {
      if (heart.dataset.galleryFavoriteBound) return;
      heart.dataset.galleryFavoriteBound = '1';
      heart.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        var btn = klaviyoFavoriteControl();
        if (btn) btn.click();
        else if (!klaviyoFavoriteGaveUp) heart.dataset.favoritePending = '1';
        if (document.activeElement && document.activeElement !== heart && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }
        if (typeof heart.focus === 'function') heart.focus({ preventScroll: true });
        syncGalleryHearts();
      });
    });
    var watch = document.querySelector('.buybox-favorites');
    if (watch && !watch.dataset.favoriteWatch) {
      watch.dataset.favoriteWatch = '1';
      new MutationObserver(syncGalleryHearts).observe(watch, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'aria-pressed', 'aria-checked']
      });
    }
    if (!document.documentElement.dataset.klaviyoFavoriteUpgrade && window.customElements && typeof customElements.whenDefined === 'function') {
      document.documentElement.dataset.klaviyoFavoriteUpgrade = '1';
      customElements.whenDefined('klaviyo-favorites-button').then(function () {
        syncGalleryHearts();
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(syncGalleryHearts);
      }).catch(function () {});
    }
    syncGalleryHearts();
    if (!klaviyoFavoriteTimer && !klaviyoFavoriteGaveUp && !klaviyoFavoriteControl() && document.querySelector('[data-gallery-favorite]')) {
      klaviyoFavoriteTimer = window.setTimeout(function () {
        klaviyoFavoriteTimer = null;
        if (klaviyoFavoriteControl()) {
          syncGalleryHearts();
          return;
        }
        klaviyoFavoriteGaveUp = true;
        syncGalleryHearts();
      }, klaviyoFavoriteWaitMs);
    }
  }

  // Highlight the size-guide row matching the shopper's currently-selected size. Sizes
  // (S, M, L, ...) won't collide with color names, so we match any selected option value
  // against each row's first cell.
  function markYourSize(dlg) {
    var table = dlg.querySelector('[data-size-table] table');
    if (!table) return;
    table.querySelectorAll('tr.is-yoursize').forEach((r) => r.classList.remove('is-yoursize'));
    var oldPill = table.querySelector('.size-guide__yoursize');
    if (oldPill) oldPill.remove();
    var form = document.querySelector('[data-buybox]');
    if (!form) return;
    var selected = Array.from(form.querySelectorAll('[data-variant-picker] input:checked'))
      .map((i) => i.value.trim().toLowerCase());
    if (!selected.length) return;
    table.querySelectorAll('tr').forEach((tr) => {
      var cell = tr.querySelector('td');
      if (!cell) return;
      if (selected.indexOf(cell.textContent.trim().toLowerCase()) === -1) return;
      tr.classList.add('is-yoursize');
      var pill = document.createElement('span');
      pill.className = 'size-guide__yoursize';
      pill.textContent = a11y('yourSize', 'Your size');
      cell.appendChild(pill);
    });
  }

  // Generic <dialog> modal opener (size guide, etc.): [data-modal-open="id"] + [data-modal-close].
  function initModals() {
    document.addEventListener('click', (e) => {
      var opener = e.target.closest('[data-modal-open]');
      if (opener) {
        var dlg = document.getElementById(opener.getAttribute('data-modal-open'));
        if (dlg && typeof dlg.showModal === 'function') {
          e.preventDefault();
          dlg.showModal();
          if (dlg.hasAttribute('data-size-guide')) markYourSize(dlg);
        }
        return;
      }
      var closer = e.target.closest('[data-modal-close]');
      if (closer) { var d = closer.closest('dialog'); if (d) d.close(); return; }
      // Click on the backdrop (the dialog element itself) closes it.
      if (e.target.tagName === 'DIALOG' && e.target.hasAttribute('open')) e.target.close();
    });
  }

  // Gallery videos: muted autoplay when the slide is in view (pause when out), with a
  // hover play/pause button and an always-visible mute/unmute toggle.
  function initGalleryVideos() {
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.querySelectorAll('[data-gallery-video]').forEach((video) => {
      if (video.dataset.vBound) return;
      video.dataset.vBound = '1';
      var slide = video.closest('[data-gallery-slide]');
      if (!slide) return;
      var toggle = slide.querySelector('[data-video-toggle]');
      var mute = slide.querySelector('[data-video-mute]');

      function syncPlay() { slide.classList.toggle('is-playing', !video.paused && !video.ended); }
      function syncMute() { slide.classList.toggle('is-muted', video.muted); }
      video.addEventListener('play', syncPlay);
      video.addEventListener('pause', syncPlay);
      video.addEventListener('ended', syncPlay);
      video.addEventListener('volumechange', syncMute);
      syncPlay();
      syncMute();

      function togglePlay() { if (video.paused) video.play().catch(() => {}); else video.pause(); }
      if (toggle) toggle.addEventListener('click', (e) => { e.stopPropagation(); togglePlay(); });
      // The video surface opens the lightbox (slide click). Inline play stays on the button.
      if (mute) mute.addEventListener('click', (e) => {
        e.stopPropagation();
        video.muted = !video.muted;
        syncMute();
        if (!video.muted && video.paused) video.play().catch(() => {});
      });

      // Muted autoplay when the slide is actually shown in the gallery viewport; pause when
      // it scrolls out. Root = the horizontal scroll viewport so its clipping is respected.
      if ('IntersectionObserver' in window && !reduce) {
        var vp = video.closest('[data-gallery-viewport]');
        new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting && en.intersectionRatio >= 0.5) video.play().catch(() => {});
            else video.pause();
          });
        }, { root: vp || null, threshold: [0, 0.5, 1] }).observe(video);
      }
    });
  }

  // Icon taps sit on the photo. Stop the gesture before it reaches the
  // gallery scroller, so a press on the heart or share does not change slides.
  // touchmove is not cancelled, so a vertical swipe that starts on a chip
  // can still scroll the page.
  function initGalleryToolPointer() {
    document.querySelectorAll('.product-gallery__tools').forEach(function (tools) {
      if (tools.dataset.toolPointer) return;
      tools.dataset.toolPointer = '1';
      function stop(e) { e.stopPropagation(); }
      tools.addEventListener('pointerdown', stop);
      tools.addEventListener('pointermove', stop);
      tools.addEventListener('pointerup', stop);
      tools.addEventListener('touchstart', stop, { passive: true });
      tools.addEventListener('touchend', stop, { passive: true });
      tools.addEventListener('touchmove', stop, { passive: true });
    });
  }

  // Empty gift blocks and a hidden share link leave the flex stack. The
  // hidden Favorites wrapper is absolute so it keeps no gap and the heart
  // can still click the Klaviyo button.
  function collapseBuyboxGhosts() {
    document.querySelectorAll('.product-buybox > .shopify-block').forEach(function (wrap) {
      var child = wrap.firstElementChild;
      var fav = null;
      var slot = null;
      var shareOff = null;
      while (child) {
        if (child.matches('.buybox-favorites--off')) fav = child;
        if (child.matches('.gwd-buybox-slot')) slot = child;
        if (child.matches('.product-share--off')) shareOff = child;
        child = child.nextElementSibling;
      }
      if (fav) wrap.classList.add('product-buybox__ghost');
      if (slot && !slot.querySelector('.gwd-buybox')) wrap.hidden = true;
      if (shareOff) wrap.hidden = true;
    });
  }

  // Buy with Prime focus ring. The host does not match :focus-visible when
  // delegatesFocus sends focus into the shadow button. data-kbd-nav is read
  // only by that host rule. Modifier keys alone do not count as navigation.
  function bindKeyboardNav() {
    var root = document.documentElement;
    if (root.dataset.kbdNavBound === '1') return;
    root.dataset.kbdNavBound = '1';
    var modifier = { Shift: 1, Control: 1, Alt: 1, Meta: 1 };
    document.addEventListener('keydown', function (event) {
      if (modifier[event.key]) return;
      root.dataset.kbdNav = '';
    });
    document.addEventListener('pointerdown', function () {
      delete root.dataset.kbdNav;
    });
  }

  function init() {
    bindKeyboardNav();
    if (!document.documentElement.dataset.pdpOptionGuard) {
      document.documentElement.dataset.pdpOptionGuard = '1';
      document.addEventListener('submit', guardUnselectedOption, true);
    }
    initHubDonateClear();
    initStickyAtc();
    initGallery();
    initGalleryVideos();
    initGalleryToolPointer();
    collapseBuyboxGhosts();
    initShare();
    initGalleryFavorite();
    initModals();
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', function (event) {
    if (!event.target || !event.target.querySelector('[data-sticky-atc], [data-buybox]')) return;
    initStickyAtc();
    initShare();
    initGalleryToolPointer();
    collapseBuyboxGhosts();
    initGalleryFavorite();
  });
})();
